import { createAsyncThunk } from "@reduxjs/toolkit";
import { ActionStatus } from "../../common/constants/action-status";
import { GENERIC_TRANSACTION_ERROR } from "../../common/constants/api-constants";
import { ApiError } from "../../common/utils/api-utils";
import { StoreState } from "../../store/store.interface";
import { updateGlobalMessage, PAYMENT_MESSAGE_ID } from "../alert/global-message";
import { MessageType } from "../alert/global-message.interface";
import { SetShippingMethodMagentoRequest } from "../checkout/shop-checkout.interface";
import { getCustomerToken } from "../customer/customer";
import { InstructorSamplingOrderError, InstructorSamplingOrderRequest, InstructorSamplingOrderSteps } from "./instructor-sample.interface";
import { estimateInstructorShippingMagentoApiCall } from "./instructor-sample-magento.api";
import { calculateTaxMagentoApiCall, createOrderMagentoApiCall } from "../checkout/shop-checkout";
import { mapInstructorShippingAndBillingAddressPayload } from "./instructor-sample-mapping";
import { getCart } from "../cart/shop-cart/store/shop-cart.selectors";

export const instructorSampleOrderThunk = createAsyncThunk(
  'instructorSample/instructorSampleOrder',
  async (payload: InstructorSamplingOrderRequest, thunkAPI) => {
    let step = InstructorSamplingOrderSteps.estimateShipping;

    try {
      const state = thunkAPI.getState() as StoreState;
      const customerToken = getCustomerToken(state);
      const cartData = getCart(state);

      if (!cartData || !cartData.products?.length) {
        throw new Error('No item in cart');
      }
      const isVirtualProduct = !!cartData.products[0].productMetadata?.virtual;

      if (!customerToken) {
        throw new Error('Customer token is not present');
      }

      const shippingEstimation = await estimateInstructorShippingMagentoApiCall(
        payload.bookstoreAddress,
        customerToken
      );

      const selectedMethod = shippingEstimation?.[0];
      if (!selectedMethod && !isVirtualProduct) {
        throw new Error('No shipping method available');
      }

      step = InstructorSamplingOrderSteps.setShippingInformation;
      const shippingInfoRequest: SetShippingMethodMagentoRequest = {
        addressInformation: {
          shipping_address: mapInstructorShippingAndBillingAddressPayload(payload.bookstoreAddress),
          billing_address: mapInstructorShippingAndBillingAddressPayload(payload.bookstoreAddress),
          ...(!isVirtualProduct && {shipping_carrier_code: selectedMethod.carrier_code}),
          ...(!isVirtualProduct && {shipping_method_code: selectedMethod.method_code}),
        },
      };

      await calculateTaxMagentoApiCall(shippingInfoRequest, customerToken);

      step = InstructorSamplingOrderSteps.placeOrder;
      const orderId = await createOrderMagentoApiCall(payload.paymentInformation, customerToken);

      return { orderId };
    } catch (error) {
      thunkAPI.dispatch(
        updateGlobalMessage({
          id: PAYMENT_MESSAGE_ID,
          message: GENERIC_TRANSACTION_ERROR,
          type: MessageType.primary,
        })
      );

      return thunkAPI.rejectWithValue(mapInstructorSubmitError(step, error));
    }
  },
  {
    condition: (_arg, { getState }) => {
      const state = getState() as StoreState;
      return state.instructorSampleState.getInstructorSampleOrder.status !== ActionStatus.loading;
    },
  }
);

const mapInstructorSubmitError = (
  step: InstructorSamplingOrderSteps,
  error: unknown
): InstructorSamplingOrderError => {
  const apiError = error as ApiError;
  return {
    step,
    code: apiError?.responseStatus,
    message: apiError?.response?.message || GENERIC_TRANSACTION_ERROR,
    raw: error,
  };
};
